import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClarificationsContext } from '@/contexts/ClarificationsContext';
import { useDebugRole } from '@/contexts/DebugRoleContext';
import { API } from '@/utils/API';

interface UseCompleteClarificationsParams {
  submissionId: string | undefined;
  refreshSubmissionForm: () => Promise<void>;
}

interface UseCompleteClarificationsResult {
  showCompleteClarifications: boolean;
  canComplete: boolean;
  handleCompleteClarifications: () => Promise<void>;
  isCompleting: boolean;
  completeError: string | null;
  isClarificationRoundActive: boolean;
}

export const useCompleteClarifications = ({
  submissionId,
  refreshSubmissionForm,
}: UseCompleteClarificationsParams): UseCompleteClarificationsResult => {
  const {
    canResolveClarifications,
    allClarificationsResolved,
    totalClarificationCount,
    clarificationRoundState,
    refetch: refetchClarifications,
  } = useClarificationsContext();

  const navigate = useNavigate();

  const { debugRole } = useDebugRole();
  const getDebugHeaders = useCallback((): Record<string, string> => {
    if (debugRole === 'real') return {};
    return { 'X-Debug-Role': debugRole };
  }, [debugRole]);

  const [isCompleting, setIsCompleting] = useState(false);
  const [completeError, setCompleteError] = useState<string | null>(null);

  const isClarificationRoundActive =
    clarificationRoundState === 'requested' ||
    clarificationRoundState === 'responded';

  const showCompleteClarifications =
    canResolveClarifications &&
    isClarificationRoundActive &&
    totalClarificationCount > 0;

  const canComplete = allClarificationsResolved;

  const handleCompleteClarifications = useCallback(async () => {
    if (!submissionId) return;
    setIsCompleting(true);
    setCompleteError(null);
    try {
      const response = await API.authenticated.fetch(
        `/submissions/${submissionId}/clarifications/complete`,
        {
          method: 'POST',
          headers: { ...getDebugHeaders(), 'Content-Type': 'application/json' },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to complete clarifications');
      }

      navigate('/submissions');
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : 'Failed to complete clarifications';
      setCompleteError(msg);
      console.error('Failed to complete clarifications:', err);
    } finally {
      setIsCompleting(false);
    }
  }, [
    submissionId,
    navigate,
    getDebugHeaders,
    refreshSubmissionForm,
    refetchClarifications,
  ]);

  return {
    showCompleteClarifications,
    canComplete,
    handleCompleteClarifications,
    isCompleting,
    completeError,
    isClarificationRoundActive,
  };
};
